import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Ban, Mail } from "lucide-react";
import { useState, useEffect } from "react";

interface CancelSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (params: { reason?: string; sendEmail: boolean }) => void;
  isPending?: boolean;
}

export function CancelSessionDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending = false,
}: CancelSessionDialogProps) {
  const [reason, setReason] = useState("");
  const [sendEmail, setSendEmail] = useState(true);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setReason("");
      setSendEmail(true);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-amber-600" />
            Termin absagen
          </DialogTitle>
          <DialogDescription>
            Der Termin wird als abgesagt markiert. Sie können optional alle Teilnehmer per E-Mail und SMS benachrichtigen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="cancelReason">Grund (optional)</Label>
            <Textarea
              id="cancelReason"
              placeholder="z.B. Krankheit des Kursleiters, Feiertag, etc."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div className="space-y-0.5">
                <Label htmlFor="sendCancelEmail">Teilnehmer benachrichtigen</Label>
                <p className="text-sm text-muted-foreground">
                  E-Mail-Benachrichtigung an alle Teilnehmer senden (SMS nur an Teilnehmer mit Einwilligung)
                </p>
              </div>
            </div>
            <Switch
              id="sendCancelEmail"
              checked={sendEmail}
              onCheckedChange={setSendEmail}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm({ reason: reason || undefined, sendEmail })}
            disabled={isPending}
          >
            {isPending ? "Wird abgesagt..." : "Termin absagen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
